import { defineStore } from 'pinia';
import { computed, ref, watch } from 'vue';
import type { Chapter } from '@/types';
import type {
  ChapterStoryContext,
  ChapterNarrativeRuleContext,
  ChapterRecurringObjectContext,
  ChapterSensorySignatureContext,
  ChapterStoryElementsContext
} from '@/types/storyContext';
import { eventBus } from '@/services/eventBus';
import { useManuscriptsStore } from '@/stores/manuscripts';
import { useWorldStore } from '@/stores/world';
import { useStoryElementsStore } from '@/stores/storyElements';

interface CreateChapterInput {
  title: string;
  number?: number;
  content?: string;
}

interface UpdateChapterInput {
  title?: string;
  content?: string;
  number?: number;
}

interface StorySummaryState {
  text: string;
  updatedAt: Date;
}

const MAX_CHAPTERS = 200;

const generateId = (): string => {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  return `ch_${Math.random().toString(36).slice(2, 10)}`;
};

const countWords = (text: string): number => {
  if (text.trim().length === 0) {
    return 0;
  }
  return text.trim().split(/\s+/u).length;
};

const determineChapterNumber = (existing: Chapter[], provided: number | undefined): number => {
  if (typeof provided === 'number' && Number.isFinite(provided) && provided > 0) {
    return Math.floor(provided);
  }

  const usedNumbers = existing
    .map((chapter) => chapter.number)
    .sort((a, b) => a - b);

  let candidate = 1;
  for (const value of usedNumbers) {
    if (value === candidate) {
      candidate += 1;
    } else if (value > candidate) {
      break;
    }
  }
  return candidate;
};

export const useChaptersStore = defineStore('chapters', () => {
  const chapters = ref<Chapter[]>([]);
  const activeChapterId = ref<string | null>(null);
  const storySummary = ref<StorySummaryState>({
    text: 'Story summary placeholder. This section will be editable in a future phase.',
    updatedAt: new Date()
  });
  const manuscriptsStore = useManuscriptsStore();

  const chapterCount = computed(() => chapters.value.length);

  const sortedChapters = computed(() => {
    return [...chapters.value].sort((a, b) => a.number - b.number);
  });

  const currentChapter = computed(() => {
    return chapters.value.find((chapter) => chapter.id === activeChapterId.value) ?? null;
  });

  const totalWordCount = computed(() => {
    return chapters.value.reduce((total, chapter) => total + chapter.wordCount, 0);
  });

  const hasReachedLimit = computed(() => chapterCount.value >= MAX_CHAPTERS);

  const extractFirstWords = (content: string, limit: number): string => {
    return content
      .trim()
      .split(/\s+/u)
      .slice(0, limit)
      .join(' ');
  };

  const createChapter = (input: CreateChapterInput): Chapter => {
    if (hasReachedLimit.value) {
      console.error('Cannot create chapter because the maximum chapter limit has been reached.', {
        chapterCount: chapterCount.value
      });
      throw new RangeError('You can only create up to 200 chapters.');
    }

    if (input.title.trim().length === 0) {
      console.error('Cannot create chapter because the title is empty.');
      throw new TypeError('Chapter title cannot be empty.');
    }

    const computedNumber = determineChapterNumber(chapters.value, input.number);
    const content = input.content ?? '';
    const now = new Date();

    const chapter: Chapter = {
      id: generateId(),
      number: computedNumber,
      title: input.title.trim(),
      content,
      wordCount: countWords(content),
      isEmpty: content.trim().length === 0,
      createdAt: now,
      updatedAt: now
    };

    const updated = [...chapters.value, chapter].sort((a, b) => a.number - b.number);
    chapters.value = updated;
    setActiveChapter(chapter.id);
    return chapter;
  };

  const updateChapter = (chapterId: string, updates: UpdateChapterInput): Chapter => {
    const index = chapters.value.findIndex((chapter) => chapter.id === chapterId);
    if (index === -1) {
      console.error('Cannot update chapter because the chapter was not found.', { chapterId });
      throw new ReferenceError('Chapter not found.');
    }

    const existing = chapters.value[index];
    const nextTitle = updates.title !== undefined ? updates.title.trim() : existing.title;
    if (nextTitle.length === 0) {
      console.error('Cannot rename chapter because the provided title is empty.', { chapterId });
      throw new TypeError('Chapter title cannot be empty.');
    }

    const nextNumber = updates.number !== undefined
      ? determineChapterNumber(chapters.value.filter((chapter) => chapter.id !== chapterId), updates.number)
      : existing.number;

    const content = updates.content !== undefined ? updates.content : existing.content;
    const updated: Chapter = {
      ...existing,
      title: nextTitle,
      number: nextNumber,
      content,
      wordCount: countWords(content),
      isEmpty: content.trim().length === 0,
      updatedAt: new Date()
    };

    const copy = [...chapters.value];
    copy[index] = updated;
    chapters.value = copy.sort((a, b) => a.number - b.number);

    if (activeChapterId.value === chapterId) {
      const manuscriptId = manuscriptsStore.activeManuscriptId?.value ?? null;
      if (manuscriptId) {
        eventBus.emit('manuscript:activeChapterChanged', {
          manuscriptId,
          chapterId
        });
      }
    }

    return updated;
  };

  const setActiveChapter = (chapterId: string | null): void => {
    activeChapterId.value = chapterId;
    const manuscriptId = manuscriptsStore.activeManuscriptId?.value ?? null;
    eventBus.emit('manuscript:activeChapterChanged', {
      manuscriptId,
      chapterId
    });
  };

  const setStorySummary = (input: { text: string }): void => {
    const sanitized = input.text.trim();
    storySummary.value = {
      text: sanitized,
      updatedAt: new Date()
    };
  };

  const getChapterById = (chapterId: string): Chapter | null => {
    return chapters.value.find((chapter) => chapter.id === chapterId) ?? null;
  };

  const getChaptersForContext = () => {
    return chapters.value.map((chapter) => ({
      id: chapter.id,
      number: chapter.number,
      title: chapter.title,
      wordCount: chapter.wordCount,
      isEmpty: chapter.isEmpty,
      summary: extractFirstWords(chapter.content, 100)
    }));
  };

  const buildStoryElementsContext = (): ChapterStoryElementsContext | null => {
    const storyElementsStore = useStoryElementsStore();
    const elements = storyElementsStore.storyElements;
    if (!elements) {
      return null;
    }

    const recurringObjectsContext: ChapterRecurringObjectContext[] = elements.recurringObjects.map(
      (object) => ({
        name: object.name,
        description: object.description,
        currentStatus: object.status,
        currentLocation: object.currentLocation,
        importance: object.importance
      })
    );

    const sensorySignaturesContext: ChapterSensorySignatureContext[] = elements.sensorySignatures.map(
      (signature) => ({
        description: signature.description,
        frequency: signature.frequency,
        applicableToCurrentScene: signature.scope === 'global',
        scope: signature.scope,
        scopeName: signature.scopeName,
        trigger: signature.trigger
      })
    );

    const narrativeRulesContext: ChapterNarrativeRuleContext[] = elements.narrativeRules.map(
      (rule) => ({
        rule: rule.rule,
        severity: rule.severity,
        scope: rule.appliesTo,
        actIds: rule.actIds && rule.actIds.length > 0 ? [...rule.actIds] : undefined,
        characterIds:
          rule.characterIds && rule.characterIds.length > 0 ? [...rule.characterIds] : undefined,
        examples:
          rule.goodExample || rule.badExample
            ? {
                good: rule.goodExample,
                bad: rule.badExample
              }
            : undefined
      })
    );

    return {
      recurringObjects: recurringObjectsContext,
      sensorySignatures: sensorySignaturesContext,
      narrativeRules: narrativeRulesContext
    };
  };

  const getChapterGenerationContext = (): ChapterStoryContext => {
    const worldStore = useWorldStore();
    return {
      world: worldStore.buildChapterWorldContext(),
      chapters: getChaptersForContext(),
      storyElements: buildStoryElementsContext()
    };
  };

  const onChapterChange = (callback: (chapterId: string) => void) => {
    watch(
      activeChapterId,
      (newId) => {
        if (newId) {
          callback(newId);
        }
      },
      { flush: 'post' }
    );
  };

  const getChapterStats = () => {
    const total = chapterCount.value;
    const completed = chapters.value.filter((chapter) => !chapter.isEmpty).length;
    const totalWords = chapters.value.reduce((sum, chapter) => sum + chapter.wordCount, 0);

    return {
      totalChapters: total,
      completedChapters: completed,
      emptyChapters: total - completed,
      totalWords,
      averageWordsPerChapter: completed > 0 ? Math.round(totalWords / completed) : 0
    };
  };

  return {
    chapters,
    activeChapterId,
    chapterCount,
    currentChapter,
    sortedChapters,
    totalWordCount,
    hasReachedLimit,
    storySummary,
    createChapter,
    updateChapter,
    setActiveChapter,
    setStorySummary,
    getChapterById,
    getChaptersForContext,
    getChapterGenerationContext,
    onChapterChange,
    getChapterStats
  };
});
